import React, {useMemo, useRef, useState} from 'react';
import {Empty, Input, Modal, Select, Spin} from "antd";
import {useTalentTeam} from "../../../providers/TalentTeamProvider";
import debounce from 'lodash/debounce';
import {useAuth} from "../../../providers/AuthProvider";
import {useAPI} from "../../../config/API";

const {Search} = Input;
const {Option} = Select;

const DebounceSelect = ({fetchOptions, debounceTimeout = 800, ...props}) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState(props.initOptions || []);
    const fetchRef = useRef(0);
    const debounceFetcher = useMemo(() => {
        const loadOptions = (value) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }
                setOptions(newOptions);
                setFetching(false);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);
    return (
        <Select
            labelInValue
            showSearch={true}
            autoClearSearchValue={true}
            onSelect={() => {
                setOptions(props.initOptions || [])
            }}
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small"/> : null}
            options={options}
            {...props}
        >
        </Select>
    );
}

// Usage of DebounceSelect

const AddTeamMemberModal = (props) => {
    const [modelOpen, setModalOpen] = useState(false);
    const {auth} = useAuth();
    const {
        team,
        isLoading,
        addTalentResourceMember,
        isUpdating
    } = useTalentTeam();
    const formRef = useRef(null)

    const {
        data: members,
        isFetching,
        isInitialLoading: isUsersLoading,
        error,
        refetch
    } = useAPI(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id],  {
        enabled: !!auth && !!auth.clientOrg?.()?.id && auth.canManageClientResourceTalent()
    });
    const [selectedOption, setSelectedOption] = useState(null);

    const teamMemberUserIds = TB.map(team.positions || [], 'user.id')
    const getOptions = (users = [], highlight = '') => {
        let highLightText = (text) => {
            if (!highlight?.length) {
                return text
            }
            const re = new RegExp(highlight, "gi");

            return text.replace(re, '<span class="font-semibold bg-warning text-black">$&</span>')
        }
        return TB.map(users, member => {
            return {
                key: member.id,
                value: member.id,
                disabled: !member.offers_service_work || !member.is_active || teamMemberUserIds.includes(member.id),
                title: member.fullName(),
                label: (
                    <span>
                        <span dangerouslySetInnerHTML={{__html: highLightText(member.fullName())}}/>

                        <small className="ml-2">
                            {!member.isRegistered() ?  (
                                <span className="text-danger italic">Unregistered</span>
                            ): (!member.offers_service_work ? (
                                <span className="text-danger italic">Not setup for teams</span>
                            ) : (!member.is_available ? (
                                <span className="text-danger italic">Unavailable</span>
                            ) : (
                                <span dangerouslySetInnerHTML={{__html: highLightText(member.email)}}/>
                            )))}</small>
                    </span>
                )
            }
        })
    }
    const searchUsers = async(value) => {
        let filtered = TB.filter(members || [], member => {
            return TB.toLower(member.fullName()).includes(TB.toLower(value)) ||
                (member.is_active && TB.toLower(member.email).includes(TB.toLower(value)))
        })

        return getOptions(filtered, value)
    }

    const initOptions = useMemo(() => {
        return getOptions(members)
    }, [members, isUsersLoading, error])

    const toggleSearchModal = async () => {
        await setModalOpen(!modelOpen)
    }

    const selectedMember = useMemo(() => {
        if (!selectedOption) return false;
        return TB.find(members || [], ['id', selectedOption?.value])
    }, [selectedOption?.value, isUsersLoading, members])

    return (
        <React.Fragment>
            <button className="btn btn-primary" onClick={toggleSearchModal}
                    disabled={!team.can_manage || isUpdating || isLoading}>
                <i className="fas fa-plus mr-2 text-white"/> Add Team Member
            </button>
            <Modal
                destroyOnClose={true}
                open={modelOpen}
                afterClose={() => setSelectedOption(undefined)}
                closable={false}
                title={null}
                footer={null}
            >
                <div className="talent-team-member-invite">

                    <div className="self-stretch flex flex-col items-stretch gap-4">
                        <div className="h-[16px] mt-[-24px] mx-[-24px]"/>
                        <p className="text-2xl font-medium">
                            Add Member to Team
                        </p>
                    </div>

                    <hr className="my-4 mx-[-24px]"/>

                    <p className="mb-4">
                        Search and select from your agency's talent resources.
                        Only members who have registered can be a part of this team.
                    </p>
                    {!isUsersLoading && (
                        <DebounceSelect
                            value={selectedOption}
                            optionLabelProp="title"
                            placeholder="Search and select from talent resources"
                            initOptions={initOptions}
                            fetchOptions={searchUsers}
                            onChange={(newValue, option) => {
                                setSelectedOption(newValue)
                            }}
                            className="w-full"
                        />
                    )}

                    {selectedMember && (
                        <div className="mt-4 flex flex-col gap-2 items-stretch">
                            <p className="font-semibold underline">
                                Available Roles
                            </p>
                            {selectedMember.talent_disciplines?.length > 0 ? (
                                <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1.5">
                                    {TB.map(selectedMember.talent_disciplines, discipline => (
                                        <div className="talent-exp-resource" key={discipline.id}>
                                            <div className="exp">
                                                {laravel.talent_scoring_options[discipline.self_score]}
                                            </div>
                                            <div className="name">
                                                {discipline.name}
                                            </div>

                                        </div>
                                    ))}
                                </div>

                            ) : (
                                <Empty className="self-center py-2 mx-auto"
                                       image={Empty.PRESENTED_IMAGE_SIMPLE}
                                       description="No roles found"
                                />
                            )}
                        </div>
                    )}

                    <div className="flex flex-row gap-3 justify-end mt-[50px]">

                        <button className="btn btn-outline-dark w-[100px]"
                                disabled={isUpdating}
                                onClick={toggleSearchModal}>
                            Cancel
                        </button>

                        <button className="btn btn-primary" disabled={isUpdating || !selectedMember}
                                onClick={async () => {
                                    let user_id = selectedMember?.id;
                                    await addTalentResourceMember(user_id)
                                    return toggleSearchModal();
                                }}>
                            Add member
                        </button>
                    </div>
                </div>

            </Modal>
        </React.Fragment>
    );
}

export default AddTeamMemberModal;